import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';
import { User } from '../models';
import { WhitelabelConfigStore } from '../core/config/whitelabel-config.store';
import { UserService } from '../services/user.service';

type BookingStatus = 'Upcoming' | 'Completed' | 'Cancelled';
type BookingFilter = 'all' | BookingStatus;

interface BookingViewModel {
  id: string;
  route: string;
  from: string;
  to: string;
  date: string;
  rawDate: string;
  time: string;
  arrivalTime: string;
  reference: string;
  amount: number;
  status: BookingStatus;
  seats: string;
  pickup: string;
  dropoff: string;
  passenger: string;
  raw: any;
}

@Component({
  selector: 'app-my-booking',
  templateUrl: './my-booking.component.html',
  styleUrls: ['./my-booking.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class MyBookingComponent implements OnInit {
  isLoggedIn = false;
  user: User | null = null;
  bookings: BookingViewModel[] = [];
  activeFilter: BookingFilter = 'all';
  gettingTrips = false;
  profileLoading = false;
  errorMessage = '';
  sidebarOpen = false;
  selectedBooking: BookingViewModel | null = null;

  readonly filters: { key: BookingFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'Upcoming', label: 'Upcoming' },
    { key: 'Completed', label: 'Completed' },
    { key: 'Cancelled', label: 'Cancelled' }
  ];

  constructor(
    private users: UserService,
    private router: Router,
    public readonly configStore: WhitelabelConfigStore
  ) {}

  ngOnInit(): void {
    const storedUser = this.readStoredUser();
    this.isLoggedIn =
      sessionStorage.getItem('isLoggedInEasy') === 'true' ||
      localStorage.getItem('isLoggedInEasy') === 'true';

    if (!this.isLoggedIn || !storedUser) {
      this.isLoggedIn = false;
      return;
    }

    this.user = new User().deserialize(storedUser);
    this.loadProfileAndTrips();
  }

  get config() {
    return this.configStore.snapshot.config;
  }

  get logo(): string {
    return this.config?.self.logoLight || this.config?.self.logo || 'assets/images/logo.webp';
  }

  get operatorName(): string {
    return this.config?.site.name || 'Mash Poa';
  }

  get userName(): string {
    const name = [this.user?.first_name, this.user?.last_name].filter(Boolean).join(' ');
    return name || 'Traveller';
  }

  get userInitials(): string {
    const initials = [this.user?.first_name, this.user?.last_name]
      .filter(Boolean)
      .map(value => String(value).trim().charAt(0).toUpperCase())
      .join('');
    return initials || 'T';
  }

  get memberSince(): string {
    const value = this.user?.created_at;
    if (!value) return 'Member account';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Member account';
    return `Member since ${new Intl.DateTimeFormat('en-KE', {
      month: 'short',
      year: 'numeric'
    }).format(date)}`;
  }

  get filteredBookings(): BookingViewModel[] {
    return this.activeFilter === 'all'
      ? this.bookings
      : this.bookings.filter(booking => booking.status === this.activeFilter);
  }

  get emptyFilterLabel(): string {
    return this.activeFilter === 'all' ? '' : `${this.activeFilter.toLowerCase()} `;
  }

  loadProfileAndTrips(): void {
    if (!this.user) return;
    this.profileLoading = true;
    this.errorMessage = '';

    this.users.getProfile(this.user).subscribe({
      next: (response: any) => {
        this.profileLoading = false;
        const profileData = response?.data || {};
        const profile = {
          ...this.user,
          ...profileData,
          // The profile endpoint does not always repeat the login token.
          token: profileData?.token || this.user?.token
        };
        this.user = new User().deserialize(profile);
        localStorage.setItem('userEasy', JSON.stringify(profile));
        sessionStorage.setItem('userEasy', JSON.stringify(profile));
        this.loadTrips();
      },
      error: () => {
        this.profileLoading = false;
        if (this.user?.phone_number) {
          this.loadTrips();
        } else {
          this.errorMessage = 'We could not load your account. Please sign in again.';
        }
      }
    });
  }

  loadTrips(): void {
    const phone = this.user?.phone_number;
    const token = this.user?.token?.trim();
    if (!phone) {
      this.errorMessage = 'Your account does not have a phone number for retrieving bookings.';
      return;
    }
    if (!token) {
      this.errorMessage = 'Your session has expired. Please sign in again to view your bookings.';
      return;
    }

    this.gettingTrips = true;
    this.errorMessage = '';
    this.users.getTrips(phone, token).subscribe({
      next: (response: any) => {
        const rows = this.bookingRows(response);
        this.bookings = rows
          .filter((trip: any) => this.isMashPoaBooking(trip))
          .map((trip: any, index: number) => this.toBookingViewModel(trip, index));
        this.gettingTrips = false;
      },
      error: () => {
        this.gettingTrips = false;
        this.errorMessage = 'We could not load your bookings. Check your connection and try again.';
      }
    });
  }

  setFilter(filter: BookingFilter): void {
    this.activeFilter = filter;
  }

  openSidebar(): void {
    this.sidebarOpen = true;
    document.body.style.overflow = 'hidden';
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
    if (!this.selectedBooking) {
      document.body.style.overflow = '';
    }
  }

  openTicket(booking: BookingViewModel): void {
    this.selectedBooking = booking;
    document.body.style.overflow = 'hidden';
  }

  closeTicket(): void {
    this.selectedBooking = null;
    if (!this.sidebarOpen) {
      document.body.style.overflow = '';
    }
  }

  stopPropagation(event: MouseEvent): void {
    event.stopPropagation();
  }

  downloadTicket(): void {
    window.print();
  }

  bookBus(): void {
    this.closeSidebar();
    this.router.navigate(['/']);
  }

  logoutUser(): void {
    localStorage.removeItem('userEasy');
    sessionStorage.removeItem('userEasy');
    sessionStorage.setItem('isLoggedInEasy', 'false');
    localStorage.setItem('isLoggedInEasy', 'false');
    document.body.style.overflow = '';
    this.router.navigate(['/']);
  }

  statusClass(status: BookingStatus): string {
    return `status-${status.toLowerCase()}`;
  }

  trackBooking(_: number, booking: BookingViewModel): string {
    return booking.id;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.selectedBooking) {
      this.closeTicket();
    } else if (this.sidebarOpen) {
      this.closeSidebar();
    }
  }

  private readStoredUser(): any | null {
    const value = sessionStorage.getItem('userEasy') || localStorage.getItem('userEasy');
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }

  private bookingRows(response: any): any[] {
    const candidates = [
      response?.rows,
      response?.results,
      response?.bookings,
      response?.data?.rows,
      response?.data?.results,
      response?.data?.bookings,
      response?.data
    ];
    return candidates.find(Array.isArray) || [];
  }

  private isMashPoaBooking(trip: any): boolean {
    const operatorObjects = [
      trip?.operator,
      trip?.bus_operator,
      trip?.booking?.operator,
      trip?.schedule?.operator,
      trip?.route?.operator,
      trip?.vehicle?.operator
    ].filter(value => value !== undefined && value !== null);

    const ids = [
      trip?.operator_id,
      trip?.operatorId,
      trip?.bus_operator_id,
      trip?.booking?.operator_id,
      trip?.schedule?.operator_id,
      ...operatorObjects.flatMap(operator =>
        typeof operator === 'object'
          ? [operator?.id, operator?.pk, operator?.operator_id, operator?.operatorId]
          : /^\d+$/.test(String(operator).trim()) ? [operator] : []
      )
    ]
      .filter(value => value !== undefined && value !== null && String(value).trim())
      .map(value => String(value).trim());

    const allowedIds = new Set(
      (this.config?.operatorIds?.length
        ? this.config.operatorIds
        : this.config?.partnerOperators?.length
          ? this.config.partnerOperators
          : ['41', '43'])
        .map(value => String(value).trim())
    );

    // When the API supplies an ID, it is authoritative. Do not allow a
    // conflicting operator through based only on a display name.
    if (ids.length) {
      return ids.every(id => allowedIds.has(id));
    }

    const names = [
      trip?.operator_alias,
      trip?.operator_name,
      trip?.operatorName,
      trip?.bus_operator,
      ...operatorObjects.flatMap(operator =>
        typeof operator === 'object'
          ? [operator?.alias, operator?.name, operator?.operator_name, operator?.slug]
          : [operator]
      )
    ]
      .map(value => this.normalizedOperatorName(value))
      .filter(Boolean);

    const allowedNames = new Set([
      this.normalizedOperatorName(this.config?.site?.name),
      this.normalizedOperatorName(this.config?.routeConfig?.appName),
      'mashpoa'
    ].filter(Boolean));

    return names.some(name => allowedNames.has(name));
  }

  private normalizedOperatorName(value: any): string {
    return String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private toBookingViewModel(trip: any, index: number): BookingViewModel {
    const from = this.firstText(
      trip?.source,
      trip?.from,
      trip?.leaving_from,
      trip?.pickup_city,
      trip?.pickup
    ) || 'Departure';
    const to = this.firstText(
      trip?.destination,
      trip?.to,
      trip?.going_to,
      trip?.dropoff_city,
      trip?.dropoff
    ) || 'Destination';
    const rawDate = this.firstText(
      trip?.departure_date,
      trip?.date_of_travel,
      trip?.travel_date,
      trip?.date
    );
    const reference = this.firstText(
      trip?.booking_reference,
      trip?.reference,
      trip?.booking_id,
      trip?.id
    ) || `Booking ${index + 1}`;
    const seats = this.seatText(trip);
    const passenger = this.firstText(
      trip?.passenger_name,
      trip?.customer_name,
      trip?.passenger?.name,
      trip?.passengers?.[0]?.name,
      this.userName
    );

    return {
      id: String(trip?.id ?? trip?.booking_id ?? reference ?? index),
      route: this.firstText(trip?.route) || `${from} → ${to}`,
      from,
      to,
      rawDate,
      date: this.formatTravelDate(rawDate),
      time: this.formatTime(this.firstText(trip?.departure_time, trip?.time)),
      arrivalTime: this.formatTime(
        this.firstText(trip?.arrival_time, trip?.arrivalTime),
        '—'
      ),
      reference,
      amount: this.numberValue(trip?.total_amount ?? trip?.amount ?? trip?.fare),
      status: this.resolveStatus(trip, rawDate),
      seats,
      pickup: this.firstText(
        trip?.pickup_location,
        trip?.pickupLocation,
        trip?.boarding_point,
        trip?.boardingPoint,
        trip?.pickup,
        from
      ),
      dropoff: this.firstText(
        trip?.dropoff_location,
        trip?.dropoffLocation,
        trip?.alighting_point,
        trip?.dropoffPoint,
        trip?.dropoff,
        to
      ),
      passenger,
      raw: trip
    };
  }

  private resolveStatus(trip: any, rawDate: string): BookingStatus {
    const rawStatus = this.firstText(
      trip?.status,
      trip?.booking_status,
      trip?.payment_status
    ).toLowerCase();
    if (
      trip?.cancelled === true ||
      trip?.is_cancelled === true ||
      /(cancel|refund|failed|void)/.test(rawStatus)
    ) {
      return 'Cancelled';
    }
    if (/(complete|travelled|used|closed)/.test(rawStatus)) {
      return 'Completed';
    }
    if (/(upcoming|confirm|paid|active|pending)/.test(rawStatus)) {
      return this.isPastDate(rawDate) ? 'Completed' : 'Upcoming';
    }
    return this.isPastDate(rawDate) ? 'Completed' : 'Upcoming';
  }

  private isPastDate(value: string): boolean {
    const date = this.parseDate(value);
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date.getTime() < today.getTime();
  }

  private formatTravelDate(value: string): string {
    const date = this.parseDate(value);
    if (!date) return value || 'Date unavailable';
    return new Intl.DateTimeFormat('en-KE', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private parseDate(value: string): Date | null {
    if (!value) return null;
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) return parsed;

    const match = value.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (!match) return null;
    const date = new Date(Number(match[3]), Number(match[2]) - 1, Number(match[1]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private formatTime(value: string, fallback = 'Time unavailable'): string {
    if (!value) return fallback;
    const match = value.match(/(\d{1,2}):(\d{2})/);
    if (!match) return value;
    const hours = Number(match[1]);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${match[2]} ${period}`;
  }

  private seatText(trip: any): string {
    const seats =
      trip?.selected_seats ??
      trip?.seat_numbers ??
      trip?.seats ??
      trip?.seat_number ??
      trip?.seat;
    if (Array.isArray(seats)) {
      const values = seats
        .map(seat => seat?.number ?? seat?.id ?? seat?.seat_number ?? seat)
        .filter(value => value !== undefined && value !== null && value !== '');
      if (values.length) return values.join(', ');
    }
    if (seats && typeof seats === 'object') {
      return String(seats.number ?? seats.id ?? seats.seat_number ?? 'Not provided');
    }
    if (seats !== undefined && seats !== null && String(seats).trim()) {
      return String(seats);
    }

    const passengerSeats = Array.isArray(trip?.passengers)
      ? trip.passengers
        .map((passenger: any) =>
          passenger?.seat_number ?? passenger?.seat?.number ?? passenger?.seat?.id
        )
        .filter(Boolean)
      : [];
    return passengerSeats.length ? passengerSeats.join(', ') : 'Not provided';
  }

  private firstText(...values: any[]): string {
    const value = values.find(item =>
      item !== undefined && item !== null && String(item).trim() !== ''
    );
    return value === undefined || value === null ? '' : String(value).trim();
  }

  private numberValue(value: any): number {
    const parsed = Number(String(value ?? 0).replace(/[^\d.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
}
